import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  Search,
  Phone,
  ArrowRight,
  Heart,
  Users,
  Shield,
  AlertTriangle,
  HelpCircle,
  BookOpen
} from "lucide-react";
import { Header, Footer } from "../../components/Layout";

const AdiccionesHubPage = () => {
  // Types of addictions with full info
  const addictionTypes = [
    {
      name: "Alcoholismo",
      description: "La adicción más común en Chile. Afecta al 10% de la población adulta y es la tercera causa de muerte evitable.",
      symptoms: ["Necesidad de beber más para el mismo efecto", "Síntomas de abstinencia", "Pérdida de control", "Problemas laborales y familiares"],
      icon: "🍺",
      color: "from-amber-500 to-orange-600",
      bgColor: "bg-amber-500/10",
      borderColor: "border-amber-500/30",
      textColor: "text-amber-400",
      path: "/adicciones/alcohol",
      stats: "10.6% de adultos afectados"
    },
    {
      name: "Cocaína y Pasta Base",
      description: "Estimulantes de alta peligrosidad. La pasta base es especialmente devastadora con adicción casi inmediata.",
      symptoms: ["Euforia intensa seguida de depresión", "Paranoia severa", "Deterioro físico rápido", "Ciclos de consumo compulsivo"],
      icon: "⚡",
      color: "from-red-500 to-rose-600",
      bgColor: "bg-red-500/10",
      borderColor: "border-red-500/30",
      textColor: "text-red-400",
      path: "/adicciones/cocaina",
      stats: "Alta tasa de mortalidad"
    },
    {
      name: "Marihuana",
      description: "Aunque se considera 'suave', puede generar dependencia real, especialmente en adolescentes y usuarios frecuentes.",
      symptoms: ["Desmotivación crónica", "Problemas de memoria", "Aislamiento social", "Deterioro académico/laboral"],
      icon: "🌿",
      color: "from-green-500 to-emerald-600",
      bgColor: "bg-green-500/10",
      borderColor: "border-green-500/30",
      textColor: "text-green-400",
      path: "/adicciones/marihuana",
      stats: "9% desarrolla dependencia"
    },
    {
      name: "Medicamentos",
      description: "Adicción a benzodiazepinas, opioides y otros fármacos recetados. Crece silenciosamente.",
      symptoms: ["Uso más allá de lo prescrito", "Búsqueda de múltiples recetas", "Síntomas de abstinencia", "Automedicación creciente"],
      icon: "💊",
      color: "from-blue-500 to-cyan-600",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/30",
      textColor: "text-blue-400",
      path: "/adicciones/medicamentos",
      stats: "En aumento post-pandemia"
    },
    {
      name: "Ludopatía",
      description: "Adicción al juego que destruye finanzas y relaciones. Incluye casinos, apuestas online y juegos de azar.",
      symptoms: ["Apuestas crecientes", "Mentiras sobre pérdidas", "Deudas ocultas", "Intentos fallidos de parar"],
      icon: "🎰",
      color: "from-purple-500 to-violet-600",
      bgColor: "bg-purple-500/10",
      borderColor: "border-purple-500/30",
      textColor: "text-purple-400",
      path: "/adicciones/ludopatia",
      stats: "2.4% de la población"
    },
    {
      name: "Adicciones Conductuales",
      description: "Tecnología, redes sociales, pornografía, compras compulsivas. Las adicciones del siglo XXI.",
      symptoms: ["Uso excesivo e incontrolable", "Interferencia con vida diaria", "Ansiedad al no acceder", "Aislamiento social"],
      icon: "📱",
      color: "from-cyan-500 to-teal-600",
      bgColor: "bg-cyan-500/10",
      borderColor: "border-cyan-500/30",
      textColor: "text-cyan-400",
      path: "/adicciones/conductuales",
      stats: "Crecimiento exponencial"
    },
    {
      name: "Pasta Base (PBC)",
      description: "Una de las drogas más devastadoras en Chile. Altamente adictiva desde los primeros consumos, requiere internación residencial.",
      symptoms: ["Deterioro físico acelerado", "Consumo compulsivo cada minutos", "Paranoia y aislamiento extremo", "Problemas respiratorios severos"],
      icon: "🚨",
      color: "from-red-600 to-rose-700",
      bgColor: "bg-red-600/10",
      borderColor: "border-red-600/30",
      textColor: "text-red-500",
      path: "/adicciones/pasta-base",
      stats: "Extrema adicción inmediata"
    },
    {
      name: "Patología Dual",
      description: "Adicción combinada con trastorno psiquiátrico (depresión, bipolaridad, ansiedad, TDAH, TLP). Requiere tratamiento integrado.",
      symptoms: ["Recaídas a pesar del tratamiento", "Síntomas psiquiátricos intensos", "Automedicación con sustancias", "Comorbilidad no diagnosticada"],
      icon: "🧠",
      color: "from-purple-500 to-indigo-600",
      bgColor: "bg-purple-500/10",
      borderColor: "border-purple-500/30",
      textColor: "text-purple-400",
      path: "/adicciones/patologia-dual",
      stats: "60% de pacientes en tratamiento"
    }
  ];

  // FAQ for SEO
  const faqs = [
    {
      question: "¿Qué es una adicción?",
      answer: "Una adicción es una enfermedad crónica del cerebro caracterizada por la búsqueda y consumo compulsivo de una sustancia o conducta, a pesar de las consecuencias negativas. Involucra cambios en los circuitos cerebrales de recompensa, motivación y memoria."
    },
    {
      question: "¿Cómo sé si tengo una adicción?",
      answer: "Las señales principales incluyen: pérdida de control sobre el consumo, tolerancia (necesitar más para el mismo efecto), síntomas de abstinencia, abandono de actividades importantes, y continuar a pesar de problemas evidentes en salud, trabajo o relaciones."
    },
    {
      question: "¿La adicción es una enfermedad o una elección?",
      answer: "La adicción es reconocida médicamente como una enfermedad crónica del cerebro. Aunque la decisión inicial de consumir puede ser voluntaria, los cambios cerebrales que produce la adicción afectan el autocontrol y la capacidad de resistir impulsos intensos."
    },
    {
      question: "¿Se puede curar la adicción?",
      answer: "La adicción es tratable y la recuperación es posible. Sin embargo, al ser una enfermedad crónica, requiere manejo continuo. Con tratamiento adecuado (generalmente 6-12 meses de tratamiento activo más 6-12 meses de seguimiento), las personas pueden recuperar el control de sus vidas y mantener la sobriedad a largo plazo."
    },
    {
      question: "¿Cuál es la adicción más peligrosa?",
      answer: "Todas las adicciones son serias, pero en términos de mortalidad y deterioro rápido, la adicción a pasta base y opioides son las más peligrosas. El alcoholismo, aunque más 'socialmente aceptado', causa más muertes totales por su alta prevalencia."
    },
    {
      question: "¿Por qué algunas personas se vuelven adictas y otras no?",
      answer: "La vulnerabilidad a la adicción depende de factores genéticos (40-60% del riesgo), ambientales (trauma, estrés, disponibilidad), edad de inicio (el cerebro adolescente es más vulnerable), y salud mental preexistente."
    }
  ];

  // Schema markup
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Tipos de Adicciones: Guía Completa",
    "description": "Información completa sobre los diferentes tipos de adicciones: alcohol, drogas, medicamentos, ludopatía y adicciones conductuales. Síntomas, efectos y tratamiento.",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": addictionTypes.map((addiction, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": addiction.name,
        "url": `https://sinadicciones.org${addiction.path}`
      }))
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Adicciones", "item": "https://sinadicciones.org/adicciones" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Tipos de Adicciones: Alcohol, Drogas, Ludopatía y más | Sin Adicciones Chile</title>
        <meta name="description" content="Guía completa sobre tipos de adicciones en Chile: alcoholismo, cocaína, marihuana, medicamentos, ludopatía y adicciones conductuales. Síntomas, efectos y cómo buscar ayuda." />
        <meta property="og:title" content="Tipos de Adicciones: Guía Completa" />
        <meta property="og:description" content="Conoce los diferentes tipos de adicciones, sus síntomas, efectos en la salud y opciones de tratamiento en Chile." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://sinadicciones.org/adicciones" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/adicciones" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      <Header />
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Adicciones</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6">
              <BookOpen className="w-5 h-5 text-purple-400" />
              <span className="text-purple-400 font-medium">Centro de Información</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Tipos de{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                Adicciones
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              Conocer es el primer paso para superar. Información completa sobre los diferentes tipos de adicciones, 
              sus síntomas, efectos y opciones de tratamiento en Chile.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/tratamientos"
                className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all shadow-lg shadow-emerald-500/25 flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Ver Tratamientos
              </Link>
              <Link 
                to="/herramientas"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <HelpCircle className="w-5 h-5" />
                Tests de Evaluación
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Addiction Types Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Explora por Tipo de Adicción
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Cada adicción tiene características únicas. Haz clic en cualquiera para conocer más sobre síntomas, efectos y tratamiento.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {addictionTypes.map((addiction, index) => (
              <Link 
                key={index}
                to={addiction.path}
                className={`group relative overflow-hidden rounded-2xl ${addiction.bgColor} border ${addiction.borderColor} hover:border-opacity-60 transition-all duration-300`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${addiction.color} opacity-0 group-hover:opacity-10 transition-opacity`} />
                
                <div className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <span className="text-4xl">{addiction.icon}</span>
                    <div className="flex-1">
                      <h3 className={`text-xl font-bold ${addiction.textColor} group-hover:brightness-110 transition-all`}>
                        {addiction.name}
                      </h3>
                      <span className="text-xs text-slate-500">{addiction.stats}</span>
                    </div>
                    <ArrowRight className={`w-5 h-5 ${addiction.textColor} opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all`} />
                  </div>
                  
                  <p className="text-slate-400 text-sm mb-4">
                    {addiction.description}
                  </p>
                  
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Señales de alerta:</p>
                    <ul className="space-y-1">
                      {addiction.symptoms.slice(0, 3).map((symptom, idx) => (
                        <li key={idx} className="text-slate-300 text-sm flex items-start gap-2">
                          <AlertTriangle className={`w-3 h-3 mt-1 ${addiction.textColor} flex-shrink-0`} />
                          <span className="line-clamp-1">{symptom}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-slate-700/50">
                    <span className={`text-sm font-medium ${addiction.textColor} flex items-center gap-1`}>
                      Conocer más
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Adicciones en Chile
            </h2>
            <p className="text-slate-400">
              Cifras que reflejan la magnitud del problema y la importancia de actuar.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { stat: "1.5M", label: "personas con consumo problemático de alcohol", color: "text-amber-400" },
              { stat: "3°", label: "causa de muerte evitable en Chile", color: "text-red-400" },
              { stat: "14 años", label: "edad promedio de inicio de consumo", color: "text-purple-400" },
              { stat: "70%", label: "de recuperación con tratamiento adecuado", color: "text-emerald-400" }
            ].map((item, index) => (
              <div key={index} className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50 text-center">
                <p className={`text-4xl font-bold ${item.color} mb-2`}>{item.stat}</p>
                <p className="text-slate-400 text-sm">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warning Signs Overview */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-red-900/30 to-slate-800/50 rounded-3xl p-8 md:p-12 border border-red-500/20">
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="w-8 h-8 text-red-400" />
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Señales Universales de Adicción
              </h2>
            </div>
            
            <p className="text-slate-300 mb-6">
              Independiente del tipo de adicción, estas señales indican que puede haber un problema:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {[
                "Pérdida de control sobre el consumo o conducta",
                "Necesidad de más cantidad para el mismo efecto",
                "Síntomas de malestar al dejar de consumir",
                "Abandono de actividades antes importantes",
                "Continuar a pesar de consecuencias negativas",
                "Mentir u ocultar el consumo a familiares",
                "Pensar constantemente en consumir",
                "Intentos fallidos de reducir o parar"
              ].map((sign, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300 text-sm">{sign}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-slate-400 mb-4">¿Te identificas con 3 o más señales?</p>
              <Link 
                to="/herramientas"
                className="inline-flex items-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl transition-all"
              >
                Hacer Test de Evaluación
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Preguntas Frecuentes
            </h2>
            <p className="text-slate-400">
              Respuestas a las dudas más comunes sobre las adicciones.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details 
                key={index}
                className="group bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <h3 className="text-lg font-semibold text-white pr-4">{faq.question}</h3>
                  <ChevronRight className="w-5 h-5 text-emerald-400 transform group-open:rotate-90 transition-transform flex-shrink-0" />
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-slate-300 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Help CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-900/50 to-slate-800/50 border border-emerald-500/20 p-8 md:p-12">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
            
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <Heart className="w-8 h-8 text-emerald-400" />
                <h2 className="text-2xl md:text-3xl font-bold text-white">
                  ¿Necesitas ayuda?
                </h2>
              </div>
              
              <p className="text-slate-300 mb-8 max-w-2xl">
                No importa qué tipo de adicción enfrentes, hay ayuda disponible. 
                Nuestros asesores expertos pueden orientarte de forma confidencial.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <a 
                  href="tel:1412"
                  className="flex items-center gap-4 p-4 bg-slate-800/80 rounded-xl border border-slate-700 hover:border-emerald-500/50 transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-400">1412</p>
                    <p className="text-slate-400 text-sm">Fono Drogas - 24/7 Gratis</p>
                  </div>
                </a>
                
                <Link 
                  to="/asesoria"
                  className="flex items-center gap-4 p-4 bg-slate-800/80 rounded-xl border border-slate-700 hover:border-emerald-500/50 transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-white">Asesoría Experta</p>
                    <p className="text-slate-400 text-sm">Te orientamos gratis</p>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Link */}
      <section className="py-16 bg-gradient-to-t from-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Shield className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            La Recuperación es Posible
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-8">
            Conoce los diferentes tipos de tratamiento disponibles y encuentra el programa adecuado para ti.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/tratamientos"
              className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/25 flex items-center gap-2 text-lg"
            >
              Ver Tipos de Tratamiento
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link 
              to="/centros-de-rehabilitacion-drogas-chile"
              className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
            >
              <Search className="w-5 h-5" />
              Buscar Centros
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdiccionesHubPage;
